import type {
  EngineAdapter,
  Layer,
  LayerKind,
  ViewerHostContext,
} from '@geosoft/viewer-sdk';
import {
  declareCapabilities,
  UnsupportedFeatureError,
  type EngineCapabilities,
} from '@/engines/contract';
import type {
  PointCloudColorMode,
  PointCloudQualitySettings,
  PointCloudQualitySnapshot,
} from '@/engines/giro3d/Giro3DAdapter';
import {
  defaultPotreeRuntimeLoader,
  type PotreeRuntime,
  type PotreeRuntimeError,
  type PotreeRuntimeLoader,
  type PotreeRuntimeQuality,
} from './potreeRuntime';

const POTREE_SUPPORTED: LayerKind[] = ['pointcloud'];

/**
 * Default quality settings — kept in lock-step with the Giro3D defaults
 * so the WP-5 `<PointCloudToolCard />` sees a consistent baseline
 * regardless of which engine is active. When the Potree runtime is
 * available these values are pushed through to its point-budget /
 * point-size APIs (see `potreeRuntime.PotreeRuntime.applyQuality`).
 */
const DEFAULT_POTREE_QUALITY: PointCloudQualitySettings = {
  pointBudget: 12_000_000,
  pointSize: 0,
  subdivisionThreshold: 0.75,
  occlusion: true,
  colorMode: 'auto',
  shape: 'square',
  intensityGamma: 0.9,
  intensityContrast: 0.15,
  intensityBrightness: 0.12,
  intensityBackground: 'dark',
};

const POTREE_CAPABILITIES: EngineCapabilities = declareCapabilities({
  engineId: 'potree',
  supportedLayerKinds: POTREE_SUPPORTED,
  supportsTime: false,
  supportsTerrain: false,
  supports3DTiles: 'none',
  supportsInterleaving: ['cesium', 'giro3d'],
  defenceVariantSafe: true,
  pluginHostKinds: [],
});

/**
 * Public state machine for the Potree adapter. See
 * `docs/design-briefs/A7-potree-runtime-integration.md` for the state
 * matrix and transitions.
 */
export type PotreeBackendStatus =
  | 'not-installed'
  | 'loading'
  | 'ready'
  | 'failed-to-load'
  | 'failed-during-render';

/** Coordinate framing surfaced to the right-rail tool card. */
export type PotreeCoordinateMode = 'georeferenced' | 'local-engineering' | 'unknown-local';

/**
 * Snapshot of adapter status the right-rail tool card consumes. Keeping
 * this distinct from `PointCloudQualitySnapshot` lets the card render
 * loading / failure / coordinate-mode badges without reaching into the
 * engine.
 */
export interface PotreeAdapterStatus {
  readonly status: PotreeBackendStatus;
  readonly coordinateMode: PotreeCoordinateMode;
  readonly layerCount: number;
  /** Populated when status is `failed-to-load` or `failed-during-render`. */
  readonly lastError: PotreeRuntimeError | null;
}

/**
 * Potree point-cloud adapter for WP-E6 multi-engine support.
 *
 * The Potree runtime package is loaded lazily through `loadRuntime`
 * (default = `defaultPotreeRuntimeLoader`). Until the loader returns a
 * runtime the adapter stays in `not-installed`, calls to
 * `setPointCloudQuality` / `setPointCloudClassificationFilter` /
 * `fitToPointClouds` are queued, and the queue is replayed when the
 * runtime transitions to `ready`. See
 * `docs/design-briefs/A7-potree-runtime-integration.md` for the design.
 */
export class PotreeAdapter implements EngineAdapter {
  id = 'potree' as const;
  readonly capabilities: EngineCapabilities = POTREE_CAPABILITIES;

  private host: ViewerHostContext | null = null;
  private status: PotreeBackendStatus = 'not-installed';
  private layers = new Map<string, Layer>();
  private quality: PointCloudQualitySettings = { ...DEFAULT_POTREE_QUALITY };
  private classificationFilter: ReadonlySet<number> = new Set<number>();
  private runtime: PotreeRuntime | null = null;
  private lastError: PotreeRuntimeError | null = null;
  private fitRequested = false;

  constructor(
    private readonly loadRuntime: PotreeRuntimeLoader = defaultPotreeRuntimeLoader,
  ) {}

  supports(kind: LayerKind): boolean {
    return POTREE_SUPPORTED.includes(kind);
  }

  getBackendStatus(): PotreeBackendStatus {
    return this.status;
  }

  /**
   * Snapshot of the adapter for the right-rail tool card. Includes the
   * runtime status, the coordinate mode of the active layers (so the
   * card can show the local-engineering badge), and the last typed
   * error when in a failure state.
   */
  getStatusSnapshot(): PotreeAdapterStatus {
    return {
      status: this.status,
      coordinateMode: this.resolveCoordinateMode(),
      layerCount: this.layers.size,
      lastError: this.lastError,
    };
  }

  async mount(host: ViewerHostContext): Promise<void> {
    this.host = host;
    this.status = 'loading';
    this.lastError = null;
    try {
      const runtime = await this.loadRuntime();
      if (runtime) {
        this.runtime = runtime;
        this.status = 'ready';
        this.replayQueuedKnobs();
      } else {
        this.runtime = null;
        this.status = 'not-installed';
      }
    } catch (cause) {
      this.runtime = null;
      const error: PotreeRuntimeError = {
        code: 'POTREE_IMPORT_FAILED',
        message:
          cause instanceof Error ? cause.message : 'Potree runtime failed to load',
        cause,
      };
      this.lastError = error;
      this.status = 'failed-to-load';
      this.host?.emit({
        type: 'error',
        message: error.message,
        cause,
      });
    }
  }

  async unmount(): Promise<void> {
    try {
      this.runtime?.dispose?.();
    } catch {
      /* dispose must never throw out of unmount */
    }
    this.runtime = null;
    this.layers.clear();
    this.host = null;
    this.status = 'not-installed';
    this.lastError = null;
    this.fitRequested = false;
  }

  async addLayer(layer: Layer): Promise<void> {
    if (!this.supports(layer.kind)) {
      throw new UnsupportedFeatureError(
        'potree',
        `layer-kind:${layer.kind}`,
        `potree adapter supports [${POTREE_SUPPORTED.join(', ')}]`,
      );
    }
    if (this.status !== 'ready') {
      throw new UnsupportedFeatureError(
        'potree',
        'runtime',
        `Potree backend is ${this.status}; cannot add layers until runtime is available`,
      );
    }
    if (isCopcUrl(layer.endpoints.pointcloud) || layer.endpoints.copc) {
      throw new UnsupportedFeatureError(
        'potree',
        'endpoints.copc',
        'COPC .copc.laz point clouds require a COPC-capable adapter, not the Potree metadata loader',
      );
    }
    const url = layer.endpoints.potree ?? layer.endpoints.pointcloud;
    if (!url) {
      throw new UnsupportedFeatureError(
        'potree',
        'endpoints.potree',
        'pointcloud layers require a Potree metadata endpoint or pointcloud alias',
      );
    }

    this.layers.set(layer.id, layer);

    if (this.runtime) {
      try {
        await this.runtime.loadCloud({ layerId: layer.id, url });
        this.runtime.applyQuality(toRuntimeQuality(this.quality, this.classificationFilter));
        if (this.fitRequested) {
          this.applyFitNow();
        }
      } catch (cause) {
        this.handleRenderFailure(layer.id, cause);
      }
    }
  }

  async removeLayer(layerId: string): Promise<void> {
    this.layers.delete(layerId);
    try {
      this.runtime?.removeCloud(layerId);
    } catch (cause) {
      this.handleRenderFailure(layerId, cause);
    }
  }

  _layerCount(): number {
    return this.layers.size;
  }

  // ---------------------------------------------------------------------------
  // WP-5 point-cloud quality contract — matches Giro3DAdapter so the
  // `<PointCloudToolCard />` can target either adapter without branching.
  // Calls made before the runtime is `ready` are recorded; the in-memory
  // snapshot is the source of truth and is replayed on transition.
  // ---------------------------------------------------------------------------

  setPointCloudQuality(settings: Partial<PointCloudQualitySettings>): void {
    this.quality = { ...this.quality, ...settings };
    if (this.runtime && this.status === 'ready') {
      try {
        this.runtime.applyQuality(toRuntimeQuality(this.quality, this.classificationFilter));
      } catch (cause) {
        this.handleRenderFailure(null, cause);
      }
    }
  }

  getPointCloudQuality(): PointCloudQualitySnapshot | null {
    if (this.layers.size === 0) return null;
    const supportedColorModes: PointCloudColorMode[] = [
      'auto',
      'rgb',
      'elevation',
      'intensity',
      'classification',
    ];
    return {
      ...this.quality,
      layerCount: this.layers.size,
      displayedPointCount: null,
      pointCount: null,
      supportedColorModes,
    };
  }

  setPointCloudClassificationFilter(classes: ReadonlySet<number>): void {
    this.classificationFilter = new Set(classes);
    if (this.runtime && this.status === 'ready') {
      try {
        this.runtime.applyClassificationMask([...this.classificationFilter]);
      } catch (cause) {
        this.handleRenderFailure(null, cause);
      }
    }
  }

  getPointCloudClassificationFilter(): ReadonlySet<number> {
    return this.classificationFilter;
  }

  /**
   * Re-frame the camera on every loaded point-cloud record. If the
   * runtime is not yet `ready`, the request is queued and replayed
   * after the next successful `addLayer` (point clouds load
   * asynchronously, so we cannot fit on an empty scene).
   */
  fitToPointClouds(): void {
    this.fitRequested = true;
    if (this.runtime && this.status === 'ready') {
      this.applyFitNow();
    }
  }

  private applyFitNow(): void {
    if (!this.runtime) return;
    try {
      const bounds = this.runtime.getCombinedBounds();
      if (!bounds) {
        // Empty scene — keep the request alive for the next add.
        return;
      }
      this.runtime.fitTo(bounds);
      this.fitRequested = false;
    } catch (cause) {
      this.handleRenderFailure(null, cause);
    }
  }

  private replayQueuedKnobs(): void {
    if (!this.runtime) return;
    try {
      this.runtime.applyQuality(toRuntimeQuality(this.quality, this.classificationFilter));
      this.runtime.applyClassificationMask([...this.classificationFilter]);
      if (this.fitRequested) {
        this.applyFitNow();
      }
    } catch (cause) {
      this.handleRenderFailure(null, cause);
    }
  }

  private handleRenderFailure(layerId: string | null, cause: unknown): void {
    const message = cause instanceof Error ? cause.message : 'Potree runtime render failure';
    const error: PotreeRuntimeError = {
      code: 'POTREE_RENDER_FAILED',
      message,
      cause,
    };
    this.lastError = error;
    this.status = 'failed-during-render';
    this.host?.emit({
      type: 'error',
      message: layerId ? `${message} (layer ${layerId})` : message,
      cause,
    });
  }

  /**
   * Resolve the coordinate framing for the active layers. The right-rail
   * card uses this to decide whether to show the local-engineering
   * badge. Order: explicit `style.coordinateMode` wins, else fall back
   * to a CRS heuristic (no CRS / `local` / `EPSG:0` -> local).
   */
  private resolveCoordinateMode(): PotreeCoordinateMode {
    let mode: PotreeCoordinateMode = 'georeferenced';
    for (const layer of this.layers.values()) {
      const explicit = readCoordinateMode(layer);
      if (explicit) {
        mode = explicit;
        if (explicit !== 'georeferenced') return explicit;
        continue;
      }
      const crs = (layer.crs ?? '').trim().toLowerCase();
      if (crs === '' || crs === 'local' || crs === 'epsg:0') {
        return 'local-engineering';
      }
    }
    return mode;
  }
}

function readCoordinateMode(layer: Layer): PotreeCoordinateMode | null {
  const raw = (layer.style as { coordinateMode?: unknown } | undefined)?.coordinateMode;
  if (typeof raw !== 'string') return null;
  if (raw === 'local-engineering' || raw === 'unknown-local' || raw === 'georeferenced') {
    return raw;
  }
  return null;
}

function toRuntimeQuality(
  settings: PointCloudQualitySettings,
  classification: ReadonlySet<number>,
): PotreeRuntimeQuality {
  return {
    pointBudget: settings.pointBudget,
    pointSize: settings.pointSize,
    subdivisionThreshold: settings.subdivisionThreshold,
    occlusion: settings.occlusion,
    classificationMask: [...classification],
  };
}

function isCopcUrl(value: string | undefined): value is string {
  return typeof value === 'string' && /\.copc(?:\.laz)?(?:[?#].*)?$/i.test(value);
}
